import { useEffect, useState, useContext } from "react";
import $ from "jquery";
import { useNavigate } from "react-router-dom";
import { Card, ListGroup, Button, Modal, Form, Accordion, Badge, Alert } from 'react-bootstrap';
import { BsFillPencilFill } from "react-icons/bs";
import Spinner from 'react-bootstrap/Spinner';
import UserContext from "../../contexts/UserContext";

const RSVPMenu = () => {
    const [menus, setMenus] = useState([]);
    const { user, setUser } = useContext(UserContext);
    const [lunchSelections, setLunchSelections] = useState({});
    const [dinnerSelections, setDinnerSelections] = useState({});
    const [noMenuMessage, setNoMenuMessage] = useState(false);
    const [assurance, setAssurance] = useState(false);
    const [currentMenu, setCurrentMenu] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    const navigate = useNavigate();
    
    const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const getUser = async (userId) => {
        try {
            const response = await $.ajax({
                type: "GET",
                url: process.env.REACT_APP_API_URL + "/getUsers/" + userId,
                xhrFields: { withCredentials: true },
                crossDomain: true
            });

            if (response && response.user) {
                setUser(response.user);
            } else {
                setUser(null);
                navigate("/login");
            }
        } catch (error) {
            console.log("Error getting user:", error);
            navigate("/login");
        }
    };

    const getData = async () => {
        setLoading(true);
        try {
            const response = await $.ajax({
                type: "GET",
                url: process.env.REACT_APP_API_URL + "/menus/rsvp",
                xhrFields: { withCredentials: true },
                crossDomain: true
            });

            if (response.menuForRsvp && response.menuForRsvp.length > 0) {
                setMenus(response.menuForRsvp);
                setNoMenuMessage(false);
                
                // Load existing RSVP data
                if (response.user && response.user.rsvpData) {
                    loadExistingRSVPs(response.user.rsvpData, response.menuForRsvp);
                }
                
                if (response.user) {
                    setUser(response.user);
                }
            } else {
                setMenus([]);
                setNoMenuMessage(true);
            }
        } catch (error) {
            console.log("Error fetching RSVP data:", error);
            if (error.status === 401) {
                navigate("/login");
            } else {
                setNoMenuMessage(true);
            }
        }
        setLoading(false);
    };

    const loadExistingRSVPs = (rsvpData, availableMenus) => {
        const newLunchSelections = {};
        const newDinnerSelections = {};

        rsvpData.forEach(rsvp => {
            const menuDate = new Date(rsvp.date).toDateString();
            
            // Find the corresponding menu to determine which items are lunch vs dinner
            const correspondingMenu = availableMenus.find(menu => 
                new Date(menu.date).toDateString() === menuDate
            );
            
            if (correspondingMenu) {
                rsvp.menu.forEach(item => {
                    const key = `${menuDate}-${item.name}`;
                    
                    // Check if this item belongs to lunch menu
                    if (correspondingMenu.lunch?.items?.includes(item.name)) {
                        newLunchSelections[key] = item.value;
                    }
                    // Check if this item belongs to dinner menu
                    else if (correspondingMenu.dinner?.items?.includes(item.name)) {
                        newDinnerSelections[key] = item.value;
                    }
                });
            }
        });

        setLunchSelections(newLunchSelections);
        setDinnerSelections(newDinnerSelections);
    };

    const handleLunchChange = (menuDate, item, value) => {
        const key = `${menuDate}-${item}`;
        setLunchSelections(prev => ({
            ...prev,
            [key]: parseFloat(value)
        }));
    };

    const handleDinnerChange = (menuDate, item, value) => {
        const key = `${menuDate}-${item}`;
        setDinnerSelections(prev => ({
            ...prev,
            [key]: parseFloat(value)
        }));
    };

    const confirmSubmit = (menu) => {
        setCurrentMenu(menu);
        setAssurance(true);
    };

    const handleClose = () => {
        setAssurance(false);
        setCurrentMenu(null);
    };

    const submitRSVP = async () => {
        if (!currentMenu) return;
        
        setSubmitting(true);
        handleClose();

        const menuDate = new Date(currentMenu.date).toDateString();
        
        // Prepare lunch items - only include items that user has explicitly selected
        const lunchItems = [];
        if (currentMenu.lunch && currentMenu.lunch.isActive && currentMenu.lunch.items) {
            currentMenu.lunch.items.forEach(item => {
                const key = `${menuDate}-${item}`;
                const quantity = lunchSelections[key];
                
                // Only include if user has made a selection (including 0 for "not needed")
                if (quantity !== undefined && quantity !== null) {
                    lunchItems.push({
                        item: item,
                        quantity: quantity
                    });
                }
            });
        }

        // Prepare dinner items - only include items that user has explicitly selected
        const dinnerItems = [];
        if (currentMenu.dinner && currentMenu.dinner.isActive && currentMenu.dinner.items) {
            currentMenu.dinner.items.forEach(item => {
                const key = `${menuDate}-${item}`;
                const quantity = dinnerSelections[key];
                
                // Only include if user has made a selection (including 0 for "not needed")
                if (quantity !== undefined && quantity !== null) {
                    dinnerItems.push({
                        item: item,
                        quantity: quantity
                    });
                }
            });
        }

        console.log("Submitting RSVP with:", { lunchItems, dinnerItems });

        try {
            const response = await $.ajax({
                type: "POST",
                url: process.env.REACT_APP_API_URL + "/menus/rsvp",
                data: JSON.stringify({
                    date: new Date(currentMenu.date).toISOString().split('T')[0],
                    lunchItems: lunchItems,
                    dinnerItems: dinnerItems
                }),
                contentType: "application/json",
                xhrFields: { withCredentials: true },
                crossDomain: true
            });

            if (response.success) {
                await getData();
            } else {
                alert("Error submitting RSVP. Please try again.");
            }
        } catch (error) {
            console.log("Error submitting RSVP:", error);
            if (error.status === 401) {
                navigate("/login");
            } else {
                alert("Error submitting RSVP. Please try again.");
            }
        }
        
        setSubmitting(false);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (date.toDateString() === today.toDateString()) {
            return "Today";
        } else if (date.toDateString() === tomorrow.toDateString()) {
            return "Tomorrow";
        } else {
            return `${weekDays[date.getDay()]}, ${date.toLocaleDateString()}`;
        }
    };

    const getFractionValue = (value) => {
        switch (value) {
            case 0: return "Not needed";
            case 0.25: return "1/2 Serving";
            case 0.5: return "1 Serving";
            case 0.75: return "1 1/2 Serving";
            case 1: return "2 Serving";
            default: return "Not selected";
        }
    };

    const hasExistingRSVP = (menuDate) => {
        if (!user || !user.rsvpData) return false;
        return user.rsvpData.some(rsvp => rsvp.date === new Date(menuDate).toDateString());
    };

    const isRSVPDisabled = (menuDate) => {
        const menuDateObj = new Date(menuDate);
        const now = new Date();
        
        // IST offset calculation
        // const ISToffSet = 330;
        // const offset = ISToffSet * 60 * 1000;
        // const istTime = new Date(now.getTime() + offset);
        const istTime = new Date(new Date().toLocaleString("en-US", {timeZone: "Asia/Kolkata"}));
        
        // Get today and tomorrow in local timezone
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        console.log("isRSVPDisabled:", menuDateObj.toDateString(), today.toDateString(), tomorrow.toDateString(), istTime);
        
        // // Check if menu is for tomorrow and it's after 6 PM IST today
        // if (menuDateObj.toDateString() === tomorrow.toDateString() && !([26,27,28,29].includes(menuDateObj.getDate()))) {
        //     return istTime.getHours() >= 18;
        // }
        
        // Check if menu is for today and it's after 6 PM IST
        if (menuDateObj.toDateString() === today.toDateString()) {
            return istTime.getHours() >= 20;
        }
        
        return false;
    };

    const renderMealSection = (menu, mealType, mealData) => {
    if (!mealData.isActive) {
        return (
            <div className="mb-3" style={{backgroundColor:'#c38154',color: '#fff3cd'}}>
                <h6 style={{color: '#fff3cd'}}>
                    {mealType === 'lunch' ? 'Lunch' : 'Dinner'}
                </h6>
                <div className="text-muted ms-3">
                    No Thaali {mealData.comment ? `- ${mealData.comment}` : ''}
                </div>
            </div>
        );
    }

    if (!mealData.items || mealData.items.length === 0) {
        return null;
    }

    const menuDate = new Date(menu.date).toDateString();
    const isDisabled = isRSVPDisabled(menu.date);

    // Get the current selection for this meal type
    // Check the first item to determine the current selection for the entire meal
    const firstItemKey = `${menuDate}-${mealData.items[0]}`;
    const currentMealSelection = mealType === 'lunch' ? 
        lunchSelections[firstItemKey] : dinnerSelections[firstItemKey];

    // Handle meal selection change - apply same selection to all items in this meal
    const handleMealSelectionChange = (value) => {
        const numericValue = parseFloat(value);
        
        // Apply the same selection to all items in this meal
        mealData.items.forEach(item => {
            if (mealType === 'lunch') {
                handleLunchChange(menuDate, item, value);
            } else {
                handleDinnerChange(menuDate, item, value);
            }
        });
    };

    return (
        <div className="mb-3" style={{backgroundColor:'#f8f9fa!important'}}>
            <h6 style={{color: '#fff3cd'}}>
                {mealType === 'lunch' ? 'Lunch' : 'Dinner'}
                <small className="ms-2 mycolor">
                    ({mealData.items.join(', ')})
                </small>
            </h6>
            
            <Accordion className="mb-2">
                <Accordion.Header>
                    {mealType === 'lunch' ? 'Lunch' : 'Dinner'} Requirement
                    {currentMealSelection !== undefined && (
                        <Badge bg="secondary" className="ms-2">
                            {getFractionValue(currentMealSelection)}
                        </Badge>
                    )}
                </Accordion.Header>
                <Accordion.Body>
                    <div className="d-flex justify-content-around p-3">
                        <span style={{color:"#c38154"}}>
                            <input 
                                type="radio" 
                                name={`${mealType}-${menuDate}`}
                                value="0" 
                                checked={currentMealSelection === 0}
                                disabled={isDisabled}
                                onChange={(e) => handleMealSelectionChange(e.target.value)}
                            /> Not needed
                        </span>
                        <span style={{color:"#c38154"}}>
                            <input 
                                type="radio" 
                                name={`${mealType}-${menuDate}`}
                                value="0.5" 
                                checked={currentMealSelection === 0.5}
                                disabled={isDisabled}
                                onChange={(e) => handleMealSelectionChange(e.target.value)}
                            /> 1 Serving (2P)
                        </span>
                    </div>
                    <div className="d-flex justify-content-around p-3">
                        <span style={{color:"#c38154"}}>
                            <input 
                                type="radio" 
                                name={`${mealType}-${menuDate}`}
                                value="1" 
                                checked={currentMealSelection === 1}
                                disabled={isDisabled}
                                onChange={(e) => handleMealSelectionChange(e.target.value)}
                            /> 2 Serving (4P)
                        </span>
                    </div>
                </Accordion.Body>
            </Accordion>
        </div>
    );
};

    useEffect(() => {
        try {
            if (!user && localStorage.getItem('userSession')) {
                getUser(JSON.parse(localStorage.getItem('userSession')));
            } else if (!user) {
                navigate("/login");
                return;
            }
        } catch (e) {
            if (!user) {
                navigate("/login");
                return;
            }
        }
        getData();
    }, []);

    if (isLoading) {
        return (
            <div className="mx-auto mt-5" style={{width: "30px"}}>
                <Spinner className="mx-auto" style={{color:'brown'}} animation="border" role="status" />
                <br/>
                <span style={{marginLeft:'-10px'}}>Loading...</span>
            </div>
        );
    }

    return (
        <>
            {noMenuMessage && (
                <Alert variant='danger'>
                    The Dashboard will open once the menu is updated for next week!
                </Alert>
            )}

            {menus.map((menu, i) => {
                const hasLunch = menu.lunch?.isActive && menu.lunch.items?.length > 0;
                const hasDinner = menu.dinner?.isActive && menu.dinner.items?.length > 0;
                const hasNoThaaliOnly = (!hasLunch && !hasDinner) && 
                    (menu.lunch?.comment || menu.dinner?.comment);
                
                if (!hasLunch && !hasDinner && !hasNoThaaliOnly) {
                    return null;
                }

                const existingRSVP = hasExistingRSVP(menu.date);
                const isDisabled = isRSVPDisabled(menu.date);

                return (
                    <Form className="mt-3 !w-lg-50" key={i}>
                        <Card className="mb-4 mx-3 !w-lg-50" >
                            <Card.Header style={{backgroundColor: '#c38154', color: 'white'}}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <span style={{fontWeight: 'normal'}}>
                                        {formatDate(menu.date)}
                                    </span>
                                    <div>
                                        {existingRSVP && <Badge bg="success">RSVP Submitted</Badge>}
                                        {isDisabled && <Badge bg="warning">Closed</Badge>}
                                    </div>
                                </div>
                            </Card.Header>
                            
                            {hasNoThaaliOnly ? (
                                <ListGroup variant="flush" style={{backgroundColor: '#c38154!important', color: 'white'}}>
                                    <ListGroup.Item >
                                        No Thaali - {menu.lunch?.comment || menu.dinner?.comment}
                                    </ListGroup.Item>
                                </ListGroup>
                            ) : (
                                <ListGroup 
                                    variant="flush" 
                                    style={existingRSVP ? {backgroundColor: '#f8f9fa'} : {}}
                                >
                                    <ListGroup.Item>
                                        {/* Lunch Section */}
                                        {menu.lunch && renderMealSection(menu, 'lunch', menu.lunch)}
                                        
                                        {/* Dinner Section */}
                                        {menu.dinner && renderMealSection(menu, 'dinner', menu.dinner)}
                                    </ListGroup.Item>
                                </ListGroup>
                            )}

                            {!hasNoThaaliOnly && !isDisabled && (
                                <Card.Link 
                                    onClick={() => confirmSubmit(menu)} 
                                    href="#" 
                                    className="m-2"
                                    style={{
                                        color: '#c38154',
                                        pointerEvents: submitting ? 'none' : 'auto',
                                        textDecoration: 'none'
                                    }}
                                >
                                    <BsFillPencilFill className="ms-2 mx-2"/> 
                                    {existingRSVP ? 'Update' : 'Submit'} Filling Request
                                </Card.Link>
                            )}
                            
                            {isDisabled && (
                                <Card.Footer style={{backgroundColor: '#fff3cd'}}>
                                    <small style={{color: '#856404'}}>
                                        RSVP closed (after 12 PM)
                                    </small>
                                </Card.Footer>
                            )}
                        </Card>
                    </Form>
                );
            })}

            <Modal centered show={assurance} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title style={{color: '#c38154'}}>Confirm RSVP</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to submit your filling request for {currentMenu ? formatDate(currentMenu.date) : ''}?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={submitRSVP} 
                        disabled={submitting}
                        style={{backgroundColor: '#c38154', borderColor: '#c38154'}}
                    >
                        {submitting ? 'Submitting...' : 'YES'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RSVPMenu;